export default class PriceUpdateLogic {
    constructor(hotelId) {
        this.hotelId = hotelId;
        this.baseApiEndpoint = infinito.vao.controller.urlHelper.goApiUrl + '/api/v1/hotels/' + hotelId + '/integration-rate-strategy/prices';
        // Track which rooms have been manually overridden during this session (not from DB)
        this.overriddenRooms = new Set();
    }

    buildApiEndpoint(effectiveDate) {
        return `${this.baseApiEndpoint}?effectiveDate=${effectiveDate}`;
    }

    extractPriceFromRateGuidance(rateGuidanceText) {
        if (!rateGuidanceText || typeof rateGuidanceText !== 'string') {
            return {minPrice: null, maxPrice: null, suggestion: 0, direction: null};
        }

        const cleanText = rateGuidanceText.trim();
        const currencyPattern = /(?:[A-Z]{2,3}|[€$£¥₹₽¢])(\d+(?:\.\d+)?)/g;
        const matches = [...cleanText.matchAll(currencyPattern)];

        if (matches.length === 0) {
            return {minPrice: null, maxPrice: null, suggestion: 0, direction: null};
        }

        const prices = matches.map(match => parseFloat(match[1]));
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);

        // Detect increase or decrease direction
        let direction = null;
        if (cleanText.toLowerCase().includes('increase')) {
            direction = 'increase';
        } else if (cleanText.toLowerCase().includes('decrease')) {
            direction = 'decrease';
        }

        // For directional guidance, use the average as the amount to add/subtract
        const suggestion = prices.length > 1 ? (minPrice + maxPrice) / 2 : prices[0];

        return {
            minPrice,
            maxPrice,
            suggestion,
            direction
        };
    }

    calculateCurrentPrice(roomData, barPrice = 0) {
        // Only return a price if backend provided one
        if (roomData.price !== null && roomData.price !== undefined) {
            return parseFloat(roomData.price);
        }

        // No price from backend - return null to show "-"
        return null;
    }

    calculateSuggestedPrice(currentPrice, rateGuidanceText, rateValue = 0) {
        const priceInfo = this.extractPriceFromRateGuidance(rateGuidanceText);

        if (priceInfo.suggestion > 0 && priceInfo.direction) {
            if (priceInfo.direction === 'increase') {
                return currentPrice + priceInfo.suggestion;
            } else if (priceInfo.direction === 'decrease') {
                return currentPrice - priceInfo.suggestion;
            }
        }

        return currentPrice || 0;
    }

    // Calculate derived price for non-BAR room based on current BAR value
    calculateDerivedPrice(barPrice, extraPriceOnBar) {
        const extra = parseFloat(extraPriceOnBar) || 0;
        return barPrice + extra;
    }

    fetchPriceData(effectiveDate) {
        const apiEndpoint = this.buildApiEndpoint(effectiveDate);
        return new Promise((resolve, reject) => {
            $.ajax({
                url: apiEndpoint,
                headers: {
                    Accept: 'application/json',
                },
                async: true,
                timeout: 300000,
                beforeSend: function beforeSend(xhr) {
                    xhr.setRequestHeader(
                        'Authorization', 'Bearer ' + infinito.vao.controller.jwtHelperV3.getAccessToken(),
                    );
                },
                success: function success(data) {
                    resolve(
                        Object.assign({}, data, {
                            added_on: new Date(),
                        }),
                    );
                },
                error: function error(data) {
                    reject(data);
                },
            });
        });
    }

    processRoomDataForModal(roomsData, rateGuidanceText, rateValue = 0) {
        if (!Array.isArray(roomsData)) {
            return [];
        }

        const processedRooms = roomsData.map(room => {
            const currentPrice = this.calculateCurrentPrice(room, rateValue);
            const hotelRoomTypeId = String(room.hotelRoomTypeId);

            return {
                ...room,
                hotelRoomTypeId,
                displayCurrentPrice: currentPrice,
                // New price starts with current price - user must manually change
                suggestedNewPrice: currentPrice,
                extraPriceOnBar: room.extraPriceOnBar || 0
            };
        });

        return processedRooms.sort((a, b) => {
            if (a.isBar && !b.isBar) return -1;
            if (!a.isBar && b.isBar) return 1;
            return 0;
        });
    }

    formatOffsetDisplay(extraPriceOnBar) {
        const offset = parseFloat(extraPriceOnBar) || 0;
        if (offset === 0) return '';
        return offset > 0 ? `+${offset.toFixed(0)}` : offset.toFixed(0);
    }

    createModalContent(processedRoomsData, rateGuidanceText, barValue, marketMetrics = {}, stayDate = '') {
        const rateGuidanceHeader = rateGuidanceText ?
            `<div class="rate-guidance-banner mb-2">
                <i class="fas fa-lightbulb text-warning mr-2"></i>
                <span class="text-muted">${rateGuidanceText}</span>
            </div>` : '';

        // BAR value - handle non-numeric values gracefully (could be "Not Selling", etc.)
        const formattedBarValue = marketMetrics.bar || '-';

        // Key metrics row - condensed with BAR included
        const pickupValue = parseFloat(marketMetrics.pickup) || 0;
        const pickupColor = pickupValue > 0 ? 'text-success' : (pickupValue < 0 ? 'text-danger' : 'text-muted');

        const metricsData = [
            { label: 'Occ', value: marketMetrics.occupancy, color: 'text-info' },
            { label: 'RTS', value: marketMetrics.roomsToSell, color: 'text-warning' },
            { label: 'Comp', value: marketMetrics.compSetAvg, color: 'text-secondary' },
            { label: 'PU', value: marketMetrics.pickup, color: pickupColor },
            { label: 'ADR', value: marketMetrics.adr, color: 'text-primary' },
            { label: 'BAR', value: formattedBarValue, color: 'text-success' }
        ];

        const metricsRow = `
            <div class="metrics-strip-compact mb-2">
                ${metricsData.map(metric => `
                    <span class="metric-inline">
                        <span class="metric-label-sm">${metric.label}:</span>
                        <span class="${metric.color} font-weight-bold">${metric.value || '-'}</span>
                    </span>
                `).join('<span class="metric-divider">|</span>')}
            </div>
        `;

        const tableRows = processedRoomsData.map(room => {
            const isActive = room.isActiveInIntegration !== false;
            const offsetDisplay = room.isBar ? '' : this.formatOffsetDisplay(room.extraPriceOnBar);
            const strategyHint = room.isBar ?
                '<span class="badge badge-warning badge-sm">BAR</span>' :
                (offsetDisplay ? `<span class="text-muted small">(BAR ${offsetDisplay})</span>` : '');

            const inactiveHint = !isActive
                ? '<span class="badge badge-secondary badge-sm ml-1" title="Not active in integration — prices won\'t be pushed"><i class="fas fa-unlink" style="font-size: 0.55rem;"></i> Inactive</span>'
                : '';

            // Handle null/undefined current price - show "-"
            const currentPriceDisplay = (room.displayCurrentPrice !== null && room.displayCurrentPrice !== undefined)
                ? room.displayCurrentPrice.toFixed(2)
                : '-';

            // New price input - default to empty if no current price
            const newPriceValue = (room.suggestedNewPrice !== null && room.suggestedNewPrice !== undefined)
                ? room.suggestedNewPrice.toFixed(2)
                : '';

            const rowClass = isActive ? 'table-row-hover' : 'table-row-hover row-inactive';

            return `
            <tr class="${rowClass}" data-room-id="${room.hotelRoomTypeId}" data-is-bar="${room.isBar}" data-extra-price="${room.extraPriceOnBar || 0}" data-active="${isActive}">
                <td class="px-2 py-2 border-bottom">
                    <div class="d-flex align-items-center flex-wrap">
                        <span class="font-weight-medium mr-2">${room.roomTypeName}</span>
                        ${strategyHint}
                        ${inactiveHint}
                    </div>
                </td>
                <td class="px-2 py-2 text-right border-bottom">
                    <span class="${room.displayCurrentPrice !== null ? 'text-success' : 'text-muted'} font-weight-bold">${currentPriceDisplay}</span>
                </td>
                <td class="px-2 py-2 border-bottom">
                    ${isActive ? `<div class="input-group input-group-sm price-input-wrapper">
                        <input type="number"
                               class="form-control form-control-sm new-price-input ${room.isBar ? 'bar-price-input' : 'derived-price-input'}"
                               value="${newPriceValue}"
                               data-room-id="${room.hotelRoomTypeId}"
                               data-is-bar="${room.isBar}"
                               data-extra-price="${room.extraPriceOnBar || 0}"
                               data-original-value="${newPriceValue}"
                               style="min-width: 90px;"
                               step="0.01"
                               min="0"
                               placeholder="Enter price">
                        <div class="override-indicator" style="display: none;" title="Manually overridden - click to reset">
                            <i class="fas fa-undo text-warning"></i>
                        </div>
                    </div>` : `<a href="#integrationRateStrategy" class="text-muted small"><i class="fas fa-cog mr-1"></i>Activate</a>`}
                </td>
            </tr>`;
        }).join('');

        return `
            <div class="price-update-modal-content">
                ${metricsRow}
                ${rateGuidanceHeader}
                
                <div class="hint-bar mb-2">
                    <small class="text-muted">
                        <i class="fas fa-info-circle mr-1"></i>
                        Prices use BAR + offset strategy. 
                        <a href="#integrationRateStrategy" class="text-primary">Configure strategy</a>
                        <span class="mx-1">|</span>
                        <a href="#compIntelligence" class="text-primary">View market data</a>
                    </small>
                </div>
                
                <div class="card shadow-sm border-0">
                    <div class="table-container" style="max-height: 350px; overflow-y: auto;">
                        <table class="table table-sm table-hover mb-0">
                            <thead class="thead-light sticky-top">
                                <tr>
                                    <th class="px-2 py-2 border-0">Room Type</th>
                                    <th class="px-2 py-2 text-right border-0">Set Price <i class="fas fa-info-circle text-muted" style="font-size: 0.7rem;" title="Price pushed from Infinito to the PMS"></i></th>
                                    <th class="px-2 py-2 border-0">New Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${tableRows}
                            </tbody>
                        </table>
                    </div>
                    
                    <div class="card-footer bg-light border-0 py-2">
                        <div class="d-flex justify-content-end align-items-center">
                            <button type="button" class="btn btn-outline-secondary btn-sm mr-2" data-dismiss="modal">
                                Cancel
                            </button>
                            <button type="button" class="btn btn-primary btn-sm" id="update-prices-btn">
                                <i class="fas fa-save mr-1"></i>Update
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            <style>
                .price-update-modal-content {
                    min-width: 280px;
                }
                /* Condensed metrics strip */
                .metrics-strip-compact {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.25rem;
                    padding: 0.4rem 0.6rem;
                    background: #f8f9fa;
                    border-radius: 6px;
                    font-size: 0.8rem;
                    align-items: center;
                    justify-content: center;
                }
                .metric-inline {
                    white-space: nowrap;
                }
                .metric-label-sm {
                    color: #6c757d;
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    margin-right: 2px;
                }
                .metric-divider {
                    color: #dee2e6;
                    margin: 0 0.3rem;
                }
                /* Rate guidance banner */
                .rate-guidance-banner {
                    padding: 0.4rem 0.6rem;
                    background: linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%);
                    border-radius: 6px;
                    font-size: 0.8rem;
                    border-left: 3px solid #ffc107;
                }
                /* Hint bar */
                .hint-bar {
                    padding: 0.3rem 0.5rem;
                    background: #e7f3ff;
                    border-radius: 4px;
                    border-left: 3px solid #007bff;
                }
                /* Table styles */
                .table-row-hover:hover {
                    background-color: #f8f9fa !important;
                }
                .table-container::-webkit-scrollbar {
                    width: 5px;
                }
                .table-container::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 3px;
                }
                .sticky-top {
                    position: sticky;
                    top: 0;
                    z-index: 10;
                }
                /* Price input styles */
                .price-input-wrapper {
                    position: relative;
                }
                .new-price-input {
                    padding-right: 28px;
                }
                .new-price-input:focus {
                    border-color: #007bff;
                    box-shadow: 0 0 0 0.15rem rgba(0, 123, 255, 0.2);
                }
                .bar-price-input {
                    border-color: #ffc107;
                    background-color: #fffdf5;
                }
                .bar-price-input:focus {
                    border-color: #ffc107;
                    box-shadow: 0 0 0 0.15rem rgba(255, 193, 7, 0.25);
                }
                /* Override indicator */
                .override-indicator {
                    position: absolute;
                    right: 6px;
                    top: 50%;
                    transform: translateY(-50%);
                    cursor: pointer;
                    z-index: 5;
                }
                .override-indicator:hover i {
                    color: #dc3545 !important;
                }
                .input-overridden {
                    background-color: #fff3cd !important;
                    border-color: #ffc107 !important;
                }
                .badge-sm {
                    font-size: 0.65rem;
                    padding: 0.2em 0.4em;
                }
                /* Inactive room row */
                .row-inactive {
                    opacity: 0.5;
                }
                tr.row-inactive td {
                    background-color: #f8f9fa;
                }
                /* Responsive */
                @media (max-width: 576px) {
                    .metrics-strip-compact {
                        font-size: 0.7rem;
                        padding: 0.3rem;
                    }
                    .metric-divider {
                        display: none;
                    }
                    .metric-inline {
                        margin: 0.1rem 0.3rem;
                    }
                }
            </style>
        `;
    }

    setupBarCascade(processedData) {
        const self = this;
        
        // Reset overridden rooms for this session
        this.overriddenRooms = new Set();
        
        // Get modal body for event delegation
        const modalBody = $('.modal-body');

        // Handle BAR input change - cascade to non-BAR rooms
        modalBody.off('input.barCascade').on('input.barCascade', '.bar-price-input', function() {
            const newBarPrice = parseFloat($(this).val()) || 0;
            
            // Update all active non-BAR rooms that haven't been manually overridden
            modalBody.find('.derived-price-input').each(function() {
                const roomId = $(this).data('room-id');
                const extraPrice = parseFloat($(this).data('extra-price')) || 0;
                const isActive = $(this).closest('tr').data('active') !== false;

                // Only update if active and not manually overridden
                if (isActive && !self.overriddenRooms.has(roomId)) {
                    const newDerivedPrice = newBarPrice + extraPrice;
                    $(this).val(newDerivedPrice.toFixed(2));
                }
            });
        });

        // Handle non-BAR input change - mark as overridden
        modalBody.off('input.derivedOverride').on('input.derivedOverride', '.derived-price-input', function() {
            const roomId = $(this).data('room-id');
            const currentVal = parseFloat($(this).val()) || 0;
            const barInput = modalBody.find('.bar-price-input');
            const barPrice = parseFloat(barInput.val()) || 0;
            const extraPrice = parseFloat($(this).data('extra-price')) || 0;
            const calculatedPrice = barPrice + extraPrice;
            
            // Check if value differs from calculated (allow small floating point differences)
            if (Math.abs(currentVal - calculatedPrice) > 0.01) {
                self.overriddenRooms.add(roomId);
                $(this).addClass('input-overridden');
                $(this).siblings('.override-indicator').show();
            } else {
                self.overriddenRooms.delete(roomId);
                $(this).removeClass('input-overridden');
                $(this).siblings('.override-indicator').hide();
            }
        });

        // Handle reset click on override indicator
        modalBody.off('click.resetOverride').on('click.resetOverride', '.override-indicator', function(e) {
            e.preventDefault();
            e.stopPropagation();
            
            const input = $(this).siblings('.derived-price-input');
            const roomId = input.data('room-id');
            const barInput = modalBody.find('.bar-price-input');
            const barPrice = parseFloat(barInput.val()) || 0;
            const extraPrice = parseFloat(input.data('extra-price')) || 0;
            const calculatedPrice = barPrice + extraPrice;
            
            // Reset to calculated value
            input.val(calculatedPrice.toFixed(2));
            self.overriddenRooms.delete(roomId);
            input.removeClass('input-overridden');
            $(this).hide();
        });
        
        // Navigation links use inline onclick handlers in the HTML
    }

    async showPriceUpdateModal(stayDate, rateGuidanceText, rateValue, marketMetrics, onUpdateCallback) {
        const self = this;
        
        // Format stay date for title
        const prettyStayDate = stayDate ? 
            window.infinito.vao.controller.dateHelper.getShortDayDateMonFullYearString(stayDate) : '';
        
        try {
            // Show modal with loading content initially
            const loadingContent = $('<div class="text-center py-4"><div class="spinner-border spinner-border-sm" role="status"></div><p class="mt-2 mb-0 small text-muted">Loading prices...</p></div>');
            window.infinito.components.basicModal.render({
                title: `Update Price - ${prettyStayDate}`,
                bodyElem: loadingContent,
                size: 'lg'
            });

            const apiResponse = await this.fetchPriceData(stayDate);
            const roomsData = apiResponse.data || [];
            const processedData = this.processRoomDataForModal(roomsData, rateGuidanceText, rateValue);

            const hasAnyStrategy = processedData.some(room => room.integrationRateStrategyId);
            if (!hasAnyStrategy) {
                const setupRequiredContent = `<div class="alert alert-warning mb-0"><i class="fas fa-exclamation-triangle mr-2"></i>Rate Integration Strategy is not configured for this hotel. Configure BAR and room strategy first in <a href="#integrationRateStrategy">Integration Rate Strategy</a>, then return to set prices.</div>`;
                const modalBody = $('.modal-body');
                if (modalBody.length > 0) {
                    modalBody.html(setupRequiredContent);
                }
                return;
            }

            const modalContent = this.createModalContent(processedData, rateGuidanceText, rateValue, marketMetrics, stayDate);

            // Update the existing modal's body content
            const modalBody = $('.modal-body');
            if (modalBody.length > 0) {
                modalBody.html(modalContent);
                // Apply modal sizing constraints - smaller width for condensed UI
                const modalDialog = $('.modal-dialog');
                if (modalDialog.length > 0) {
                    modalDialog.css({
                        'max-width': '550px',
                        'min-width': '320px',
                        'margin': '1.75rem auto',
                        'width': 'calc(100% - 2rem)'
                    });
                }
            } else {
                window.infinito.components.basicModal.render({
                    title: `Update Price - ${prettyStayDate}`,
                    bodyElem: $(modalContent),
                    isTitleHtml: false,
                    size: 'lg'
                });
            }

            // Setup BAR cascade behavior and link handlers
            this.setupBarCascade(processedData);

            setTimeout(() => {
                const updateBtn = document.getElementById('update-prices-btn');
                if (updateBtn) {
                    updateBtn.addEventListener('click', () => {
                        this.handleUpdatePrices(processedData, stayDate, onUpdateCallback);
                    });
                }
            }, 100);

        } catch (error) {
            console.error('Error loading price data:', error);
            const errorContent = '<div class="alert alert-danger mb-0">Failed to load price data. Please try again.</div>';
            const modalBody = $('.modal-body');
            if (modalBody.length > 0) {
                modalBody.html(errorContent);
            } else {
                window.infinito.components.basicModal.render({
                    title: 'Error',
                    bodyElem: $(errorContent)
                });
            }
        }
    }

    formatDateToDDMMYYYY(dateString) {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
    }

    submitPriceUpdates(priceUpdates, effectiveDate) {
        return new Promise((resolve, reject) => {
            const requestBody = priceUpdates.map(update => {
                const requestItem = {
                    integrationRateStrategyId: String(update.integrationRateStrategyId),
                    price: String(update.newPrice),
                    effectiveDate: this.formatDateToDDMMYYYY(effectiveDate)
                };

                if (update.priceId) {
                    requestItem.id = String(update.priceId);
                }

                return requestItem;
            });

            $.ajax({
                url: this.baseApiEndpoint,
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                data: JSON.stringify(requestBody),
                async: true,
                timeout: 300000,
                beforeSend: function beforeSend(xhr) {
                    xhr.setRequestHeader(
                        'Authorization', 'Bearer ' + infinito.vao.controller.jwtHelperV3.getAccessToken(),
                    );
                },
                success: function success(data) {
                    resolve(data);
                },
                error: function error(data) {
                    reject(data);
                },
            });
        });
    }

    async handleUpdatePrices(processedData, stayDate, onUpdateCallback) {
        // Only submit active rooms
        const activeRooms = processedData.filter(room => room.isActiveInIntegration !== false);
        const missingStrategyRooms = activeRooms.filter(room => !room.integrationRateStrategyId);
        if (missingStrategyRooms.length > 0) {
            const modalBody = $('.modal-body');
            if (modalBody.length > 0) {
                modalBody.html('<div class="alert alert-warning mb-0"><i class="fas fa-exclamation-triangle mr-2"></i>Some room types are missing Integration Rate Strategy mapping. Configure strategy first, then retry price update.</div>');
            }
            return;
        }

        const inputs = document.querySelectorAll('.new-price-input');
        const updatedPrices = [];

        inputs.forEach(input => {
            const roomId = input.getAttribute('data-room-id');
            const newPrice = parseFloat(input.value) || 0;
            const roomData = activeRooms.find(room => room.hotelRoomTypeId === roomId);

            if (roomData) {
                updatedPrices.push({
                    hotelRoomTypeId: roomId,
                    roomTypeName: roomData.roomTypeName,
                    oldPrice: roomData.displayCurrentPrice,
                    newPrice: newPrice,
                    priceId: roomData.priceId,
                    integrationRateStrategyId: roomData.integrationRateStrategyId
                });
            }
        });

        if (updatedPrices.length === 0) {
            const modalBody = $('.modal-body');
            if (modalBody.length > 0) {
                modalBody.html('<div class="alert alert-danger mb-0"><i class="fas fa-exclamation-circle mr-2"></i>No valid room prices to update. Please refresh and try again.</div>');
            }
            return;
        }

        try {
            // Show loading state
            const modalBody = $('.modal-body');
            if (modalBody.length > 0) {
                modalBody.html('<div class="text-center py-4"><div class="spinner-border spinner-border-sm" role="status"></div><p class="mt-2 mb-0 small text-muted">Updating prices...</p></div>');
            }

            // Submit price updates via POST API
            await this.submitPriceUpdates(updatedPrices, stayDate);

            // Show success message
            if (modalBody.length > 0) {
                modalBody.html('<div class="alert alert-success mb-0"><i class="fas fa-check-circle mr-2"></i>Prices updated successfully!</div>');
            }

            // Call the callback with updated prices
            if (onUpdateCallback) {
                onUpdateCallback(updatedPrices);
            }

            // Close modal after a short delay
            setTimeout(() => {
                window.infinito.components.basicModal.close();
            }, 1200);

        } catch (error) {
            console.error('Error updating prices:', error);

            // Show error message
            const modalBody = $('.modal-body');
            if (modalBody.length > 0) {
                modalBody.html('<div class="alert alert-danger mb-0"><i class="fas fa-exclamation-circle mr-2"></i>Failed to update prices. Please try again.</div>');
            }
        }
    }

    // Cleanup event handlers when modal is closed
    cleanup() {
        const modalBody = $('.modal-body');
        modalBody.off('input.barCascade');
        modalBody.off('input.derivedOverride');
        modalBody.off('click.resetOverride');
        this.overriddenRooms.clear();
    }
}

window.vao = window.vao || {};
window.vao.components = window.vao.components || {};
window.vao.components.PriceUpdateLogic = PriceUpdateLogic;
customElements.define('vao-page-desktop-single-price-update', PriceUpdateLogic);
